import { computed } from "vue";
import { defineStore } from "pinia";
import { usePrimitiveModelWithChanges } from "@/features/core-shared/composables/usePrimitiveModelWithChanges";
import { useZodValidation } from "@/features/core-shared/composables/useZodValidation";
import { experienceTitleSchema } from "@/features/experience-shared/schemas/experience-title.schema";
import { useOperationalTranslation } from "@/features/experience-curation/composables/useOperationalTranslation";
import { useHasOperationalTitleChange } from "@/features/experience-curation/queries/useHasRawFieldChange";
import { areTranslationOperationalTitleFieldsUpdated } from "@/features/experience-translation/sidebar/lib/field-update-helpers";

export function createOperationalTitleField(operationalId: string, languageCode: string = "en") {
  return defineStore(`operational-title-field-${operationalId}-${languageCode}`, () => {
    const { $t } = useNuxtApp();

    const { translation } = useOperationalTranslation(operationalId, languageCode);
    const initialValue = computed(() => translation.value?.name ?? "");

    const { model, hasChanges, isFilled, reset } = usePrimitiveModelWithChanges(initialValue);
    const { isValid, errors } = useZodValidation(experienceTitleSchema, model);
    const errorMessage = computed(() => $t(errors.value?.errors?.join(",") || ""));

    const hasUnpublishedChange =
      languageCode === "en"
        ? useHasOperationalTitleChange(operationalId)
        : computed(() => areTranslationOperationalTitleFieldsUpdated(operationalId));

    return {
      id: "operational-title",
      model,
      hasChanges,
      isFilled,
      isValid,
      isRequired: true,
      errors,
      errorMessage,
      hasUnpublishedChange,
      reset,
    };
  });
}
